import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CalendarClock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { API, POLL_MS, TA_STATUS, fmtTime, errMsg } from './taUtils';
import { getLocalToday } from '../../utils/dateUtils';

const BAR = { green: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-red-500' };
const ms = (iso) => (iso ? new Date(iso).getTime() : null);

function DayRow({ it, scale, showPre, tz, onOpen }) {
    const bar = (a, b, cls, tid) => {
        if (!a || !b) return null;
        const l = scale(ms(a));
        return <div className={`absolute rounded-sm ${cls}`} style={{ left: `${l}%`, width: `${Math.max(scale(ms(b)) - l, 0.4)}%` }} data-testid={tid} />;
    };
    return (
        <button type="button" onClick={() => onOpen(it)} className="w-full flex items-center border-b border-border/20 hover:bg-muted/10 text-left" style={{ height: 40 }} data-testid={`day-row-${it.flight_id}`}>
            <div className="w-44 sm:w-56 shrink-0 px-2">
                <p className="text-xs font-bold font-mono">{it.flight_number} <span className="font-normal text-muted-foreground">{it.origin}→{it.destination}</span></p>
                <p className="text-[10px] text-muted-foreground font-mono">{it.aircraft_registration || '—'} · {TA_STATUS[it.status]} · {it.done_count}/{it.total_count}</p>
            </div>
            <div className="relative flex-1 h-full">
                {showPre && bar(it.pre_start, it.planned_start, 'top-[8px] h-2 bg-sky-500/15 border border-sky-500/30', `day-pre-${it.flight_id}`)}
                {bar(it.planned_start, it.planned_end, 'top-[8px] h-2 border border-slate-400/60 bg-slate-500/10', `day-plan-${it.flight_id}`)}
                {it.standard_end && <div className="absolute top-[4px] h-[30px] border-l-2 border-dashed border-red-400/70" style={{ left: `${scale(ms(it.standard_end))}%` }} title="Fin estándar" />}
                {it.arrived && bar(it.anchor_time, it.actual_end, `top-[20px] h-2.5 ${BAR[it.color]} ${it.status === 'en_curso' ? 'animate-pulse' : ''}`, `day-actual-${it.flight_id}`)}
                {it.elapsed_min !== null && it.elapsed_min !== undefined && (
                    <span className="absolute top-[18px] text-[9px] font-mono text-muted-foreground" style={{ left: `calc(${scale(ms(it.actual_end || it.planned_end))}% + 4px)` }}>{Math.round(it.elapsed_min)}/{it.standard_minutes}</span>
                )}
                {it.delay_cause && <span className="absolute right-1 top-[2px] text-[9px] text-red-700 dark:text-red-300 truncate max-w-[40%]">{it.delay_cause.cause}</span>}
            </div>
            <span className="w-14 shrink-0 text-[10px] font-mono text-right pr-2 text-muted-foreground">{fmtTime(it.scheduled_departure, tz)}</span>
        </button>
    );
}

export function DayGantt() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const headers = { Authorization: `Bearer ${token}` };
    const [date, setDate] = useState(getLocalToday());
    const [aircraft, setAircraft] = useState('all');
    const [base, setBase] = useState('all');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showPre, setShowPre] = useState(null);

    const load = useCallback(async () => {
        try {
            const p = new URLSearchParams({ date });
            if (aircraft !== 'all') p.set('aircraft_id', aircraft);
            if (base !== 'all') p.set('base', base);
            const res = await axios.get(`${API}/turnaround/day?${p}`, { headers });
            setData(res.data);
            setShowPre(prev => (prev === null ? res.data.settings?.show_pre_arrival !== false : prev));
        } catch (e) { toast.error(errMsg(e, 'Error al cargar la vista del día')); }
        finally { setLoading(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, aircraft, base, token]);
    useEffect(() => { setLoading(true); load(); }, [load]);
    useAutoRefresh(load, POLL_MS);

    const items = data?.items || [];
    const pts = items.flatMap(i => [showPre ? i.pre_start : i.planned_start, i.planned_end, i.standard_end, i.actual_end, i.scheduled_departure]).filter(Boolean).map(ms);
    const lo = pts.length ? Math.min(...pts) - 10 * 60000 : 0;
    const hi = pts.length ? Math.max(...pts) + 10 * 60000 : 1;
    const scale = (t) => ((Math.min(Math.max(t, lo), hi) - lo) / (hi - lo)) * 100;
    const ticks = [];
    if (pts.length) { const h = 3600000; for (let t = Math.ceil(lo / h) * h; t <= hi; t += h) ticks.push(t); }
    const nowT = ms(data?.now);

    return (
        <div className="space-y-4" data-testid="day-gantt">
            <div className="flex flex-wrap items-center gap-2">
                <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-[160px] font-mono" data-testid="day-date" />
                <Select value={aircraft} onValueChange={setAircraft}>
                    <SelectTrigger className="w-[170px]" data-testid="day-filter-aircraft"><SelectValue placeholder="Aeronave" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Todas las aeronaves</SelectItem>{(data?.aircraft || []).map(a => <SelectItem key={a.id} value={a.id}>{a.registration}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={base} onValueChange={setBase}>
                    <SelectTrigger className="w-[150px]" data-testid="day-filter-base"><SelectValue placeholder="Base" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">Todas las bases</SelectItem>{(data?.bases || []).map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
                <label className="flex items-center gap-2 text-xs text-muted-foreground ml-auto"><Switch checked={!!showPre} onCheckedChange={setShowPre} data-testid="day-toggle-pre" />Previas a la llegada</label>
                <span className="text-xs text-muted-foreground flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />Actualiza cada 15 s</span>
            </div>
            <Card className="card-aviation"><CardContent className="p-3">
                {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                    : !items.length ? <div className="py-10 text-center text-muted-foreground" data-testid="day-empty"><CalendarClock className="w-10 h-10 mx-auto mb-2 opacity-30" />No hay turnarounds para este día (solo se generan para vuelos de hoy en adelante).</div>
                        : (
                            <div className="overflow-x-auto"><div className="min-w-[760px]">
                                <div className="flex text-[10px] font-mono text-muted-foreground h-5">
                                    <div className="w-44 sm:w-56 shrink-0 px-2">Vuelo</div>
                                    <div className="relative flex-1">{ticks.map(t => <span key={t} className="absolute -translate-x-1/2" style={{ left: `${scale(t)}%` }}>{fmtTime(new Date(t).toISOString(), data.timezone)}</span>)}</div>
                                    <div className="w-14 shrink-0 text-right pr-2">Salida</div>
                                </div>
                                <div className="relative">
                                    <div className="absolute inset-0 flex pointer-events-none"><div className="w-44 sm:w-56 shrink-0" /><div className="relative flex-1">
                                        {ticks.map(t => <div key={t} className="absolute top-0 bottom-0 border-l border-border/20" style={{ left: `${scale(t)}%` }} />)}
                                        {nowT >= lo && nowT <= hi && <div className="absolute top-0 bottom-0 border-l-2 border-primary" style={{ left: `${scale(nowT)}%` }} data-testid="day-now-line" />}
                                    </div><div className="w-14 shrink-0" /></div>
                                    {items.map(it => <DayRow key={it.flight_id} it={it} scale={scale} showPre={showPre} tz={data.timezone} onOpen={(i) => navigate(`/turnaround/flight/${i.flight_id}`)} />)}
                                </div>
                            </div></div>
                        )}
                <div className="flex flex-wrap gap-4 mt-3 text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1"><span className="w-4 h-2 border border-slate-400/60 bg-slate-500/10 rounded-sm" />Planificado</span>
                    <span className="flex items-center gap-1"><span className="w-4 h-2 bg-emerald-500 rounded-sm" />Real dentro del tiempo</span>
                    <span className="flex items-center gap-1"><span className="w-4 h-2 bg-amber-500 rounded-sm" />Cerca del límite</span>
                    <span className="flex items-center gap-1"><span className="w-4 h-2 bg-red-500 rounded-sm" />Excedido</span>
                    <span className="flex items-center gap-1"><span className="h-3 border-l-2 border-dashed border-red-400" />Fin del tiempo estándar</span>
                </div>
            </CardContent></Card>
        </div>
    );
}
